import { useEffect, useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import { useCookie } from '@/Cookies/useCookies'
import { startAdminSsoLogin } from '@/lib/auth/adminLogin'
import { ADMIN_SESSION_COOKIE } from '@/lib/auth/adminCookieNames'
import {
  clearAdminClientAuthSession,
  clearClientAuthSession,
} from '@/lib/auth/clientSession'
import { AUTH_API_PATHS } from '@/lib/auth/paths'
import {
  broadcastAuthTabSyncEvent,
  parseAuthTabSyncEvent,
  AUTH_TAB_SYNC_KEY,
} from '@/lib/auth/tabSync'
import { useImpersonationStatus } from '@/Auth/useImpersonationStatus'
import {
  classifyAdminVerifyResponse,
  type AdminVerifyResult,
} from '@/lib/auth/adminVerifyResponse'
import { fetchWithCloudflarePreclearance } from '@/lib/cloudflarePreclearance'

export type AdminUser = {
  firstName: string
  lastName: string
  email: string
}

type VerifyResponse = AdminVerifyResult<AdminUser>

let verifyInFlight: Promise<VerifyResponse> | null = null

function verifyAuthGated(): Promise<VerifyResponse> {
  if (verifyInFlight) return verifyInFlight

  verifyInFlight = (async () => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    try {
      const response = await fetchWithCloudflarePreclearance(
        AUTH_API_PATHS.VERIFY_ADMIN,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        }
      )

      return await classifyAdminVerifyResponse<AdminUser>(response)
    } finally {
      clearTimeout(timeoutId)
      verifyInFlight = null
    }
  })()

  return verifyInFlight
}

export function useAdminAuth({
  verifyWithoutSessionMarker = false,
}: UseAdminAuthOptions = {}) {
  const [isResolved, setIsResolved] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null)
  const router = useRouter()
  const { setCookie, getCookie } = useCookie()
  const { isImpersonatingCurrentUser } = useImpersonationStatus()

  const adminSessionCookie = getCookie(ADMIN_SESSION_COOKIE)
  const hasAdminSession = !!adminSessionCookie

  const clearAdminCookies = useCallback(() => {
    setCookie(ADMIN_SESSION_COOKIE, '', 0)
    clearAdminClientAuthSession()
    setIsAuthenticated(false)
    setAdminUser(null)
  }, [setCookie])

  const verifyAuth = useCallback(async () => {
    try {
      const data = await verifyAuthGated()
      if (!data.success && data.authFailure) {
        clearAdminCookies()
      } else if (data.success) {
        setIsAuthenticated(true)
        if (data.user) {
          setAdminUser(data.user)
        }
      }
    } catch {
      // Network and intermediary failures are transient. Preserve the session
      // marker and retry on the next verification interval.
    } finally {
      setIsResolved(true)
    }
  }, [clearAdminCookies])

  const signIn = async (redirectPath = '/admin'): Promise<boolean> => {
    startAdminSsoLogin({ redirectPath })
    return true
  }

  const signOut = useCallback(async () => {
    const isImpersonating = isImpersonatingCurrentUser

    await fetchWithCloudflarePreclearance(AUTH_API_PATHS.ADMIN_SIGNOUT, {
      method: 'POST',
    }).catch(() => {})
    clearAdminCookies()
    if (isImpersonating) {
      clearClientAuthSession()
      broadcastAuthTabSyncEvent('customer', 'signout')
    }
    broadcastAuthTabSyncEvent('admin', 'signout')
    router.push('/admin/login')
  }, [clearAdminCookies, isImpersonatingCurrentUser, router])

  useEffect(() => {
    if (!hasAdminSession && !verifyWithoutSessionMarker) {
      queueMicrotask(() => {
        setIsAuthenticated(false)
        setAdminUser(null)
        setIsResolved(true)
      })
      return
    }

    queueMicrotask(() => {
      void verifyAuth()
    })

    const interval = setInterval(() => {
      void verifyAuth()
    }, 60000)
    return () => clearInterval(interval)
  }, [hasAdminSession, verifyAuth, verifyWithoutSessionMarker])

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== AUTH_TAB_SYNC_KEY) return

      const authEvent = parseAuthTabSyncEvent(event.newValue)
      if (!authEvent || authEvent.scope !== 'admin') return

      if (authEvent.action === 'signin') {
        void verifyAuth().then(() => {
          router.refresh()
        })
        return
      }

      clearAdminCookies()
      const pathname = window.location.pathname || '/'
      if (pathname.startsWith('/admin') && pathname !== '/admin/login') {
        router.push('/admin/login')
      } else {
        router.refresh()
      }
    }

    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [clearAdminCookies, router, verifyAuth])

  return { isAuthenticated, isResolved, adminUser, signIn, signOut }
}

interface UseAdminAuthOptions {
  verifyWithoutSessionMarker?: boolean
}
